import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { playDrop } from "../utils/waterDropSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SqueegeeReveal — signature scroll-driven reveal.
 * As the user scrolls, a squeegee wipes across a dirty-glass overlay
 * revealing the crystal-clear building behind. Reinforces our brand story:
 * precision, water, glass — the actual work we do.
 *
 * Technique: two stacked images (dirty + clean) with a canvas-drawn wet-glass
 * overlay whose opacity is tied to scroll progress via CSS clip-path.
 */
export default function SqueegeeReveal({
  headline = "Streak-free.",
  italic = "Every pane. Every time.",
  copy = "Deionised water. Carbon-fiber poles. Zero total dissolved solids. When our glass dries, it dries perfectly clear — no bucket rings, no ladder marks, no callbacks.",
  stat = { value: "0 TDS", label: "Trace-free finish" },
}) {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1
  const [cleanImage, setCleanImage] = useState("");
  const soundFiredRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data?.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = raw["transform-2-after"];
      if (url) setCleanImage(url.startsWith("http") ? url : `${base}${url}`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    function onScroll() {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Section is "active" while it's within viewport travel range
      const start = rect.top - vh * 0.4;         // start when section is 40% into viewport
      const end = rect.top + rect.height - vh * 0.9; // finish before it exits
      const raw = 1 - (end / (rect.height * 0.55));
      // clamp
      const p = Math.max(0, Math.min(1, raw));
      // Alternative simpler calc:
      const total = rect.height + vh;
      const seen = vh - rect.top;
      const p2 = Math.max(0, Math.min(1, seen / total));
      // Use p2 which is more predictable
      setProgress(p2);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Squeegee wipes from left → right as progress goes 0..1
  // 0..0.2 = build-up, 0.2..0.8 = wipe travel, 0.8..1 = settled
  const wipe = Math.max(0, Math.min(1, (progress - 0.15) / 0.55));
  const wipePercent = wipe * 100;
  // Squeegee handle position tracks the wipe edge
  const squeegeeLeft = `${wipePercent}%`;

  // Fire a single water-drop sound cue as the squeegee completes the wipe
  useEffect(() => {
    if (wipe >= 0.92 && !soundFiredRef.current) {
      soundFiredRef.current = true;
      playDrop({ pitch: 1.05, volume: 0.5 });
    } else if (wipe < 0.4) {
      // reset when user scrolls back up
      soundFiredRef.current = false;
    }
  }, [wipe]);

  // "Dirty" overlay opacity fades as wipe advances (past = clean)
  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ background: "var(--bg)", padding: "120px 0" }}
      data-testid="squeegee-reveal"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        {/* Header rail */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-3 overline pt-2">The squeegee test</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              {headline}{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>{italic}</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left — the interactive glass frame (8 cols) */}
          <div className="lg:col-span-8">
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: "16 / 10",
                borderRadius: "28px",
                border: "1px solid var(--line-gold)",
                boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03) inset",
              }}
            >
              {/* Clean building layer (bottom) — from admin-uploaded transform-2-after */}
              {cleanImage ? (
                <img
                  src={cleanImage}
                  alt="Clean glass building"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "brightness(0.72) contrast(1.08) saturate(0.95)" }}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(180deg, #12121a 0%, #0a0a0d 100%)",
                }} />
              )}

              {/* Reflective sheen after wipe */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                style={{
                  opacity: wipe > 0.4 ? 0.55 : 0,
                  background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.14) 45%, rgba(255,255,255,0.28) 50%, rgba(255,255,255,0.14) 55%, transparent 70%)",
                  mixBlendMode: "screen",
                }}
              />

              {/* Dirty-glass overlay — clipped by squeegee wipe */}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  clipPath: `polygon(${wipePercent}% 0, 100% 0, 100% 100%, ${wipePercent}% 100%)`,
                  transition: "clip-path 0.05s linear",
                }}
              >
                {/* Base grime tone */}
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(180deg, rgba(60,60,55,0.72) 0%, rgba(80,72,58,0.68) 100%)",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                }} />
                {/* Water spots pattern (SVG data-uri) */}
                <div
                  className="absolute inset-0 opacity-70"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.045' numOctaves='2' stitchTiles='stitch' seed='7'/><feColorMatrix values='0 0 0 0 0.85  0 0 0 0 0.82  0 0 0 0 0.75  0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
                    mixBlendMode: "overlay",
                  }}
                />
                {/* Random water spots */}
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                  {[
                    [12, 22, 2.4], [28, 15, 1.6], [41, 33, 3.1], [55, 12, 2.1], [72, 28, 2.6],
                    [88, 20, 1.9], [18, 48, 2.7], [37, 55, 1.9], [52, 62, 2.4], [64, 50, 2.2],
                    [78, 65, 3.0], [92, 55, 1.7], [22, 78, 2.5], [42, 85, 1.9], [60, 82, 2.8],
                    [80, 88, 2.1], [8, 65, 1.8], [95, 78, 2.3],
                  ].map(([cx, cy, r], i) => (
                    <circle key={i} cx={cx} cy={cy} r={r}
                      fill="rgba(255,255,255,0.22)"
                      stroke="rgba(255,255,255,0.35)"
                      strokeWidth="0.2"
                    />
                  ))}
                </svg>
              </div>

              {/* Squeegee itself — travels with the wipe edge */}
              <div
                aria-hidden
                className="absolute top-0 bottom-0"
                style={{
                  left: squeegeeLeft,
                  width: "4px",
                  transform: "translateX(-2px)",
                  background: "linear-gradient(180deg, rgba(198,168,124,0), rgba(198,168,124,0.7) 20%, rgba(232,217,197,0.95) 50%, rgba(198,168,124,0.7) 80%, rgba(198,168,124,0))",
                  boxShadow: "0 0 24px rgba(232,217,197,0.4), 0 0 40px rgba(198,168,124,0.25)",
                  opacity: wipe > 0.02 && wipe < 0.99 ? 1 : 0,
                  transition: "opacity 0.4s ease",
                }}
              />
              {/* Squeegee handle (industrial styling above blade) */}
              <div
                aria-hidden
                className="absolute"
                style={{
                  left: squeegeeLeft,
                  top: "-12px",
                  transform: "translateX(-14px)",
                  width: "28px",
                  height: "36px",
                  borderRadius: "6px",
                  background: "linear-gradient(180deg, #2b2c30 0%, #16171a 100%)",
                  border: "1px solid rgba(198,168,124,0.35)",
                  opacity: wipe > 0.02 && wipe < 0.99 ? 1 : 0,
                  transition: "opacity 0.4s ease",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
                }}
              />

              {/* Water beads dripping just after squeegee (right of wipe edge) */}
              <div
                aria-hidden
                className="absolute top-0 bottom-0"
                style={{
                  left: `calc(${squeegeeLeft} - 22px)`,
                  width: "22px",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.10))",
                  opacity: wipe > 0.03 && wipe < 0.98 ? 0.8 : 0,
                  transition: "opacity 0.4s ease",
                  pointerEvents: "none",
                }}
              />

              {/* Progress meter overlay */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text)" }}>
                <span style={{ opacity: 0.75 }}>Before</span>
                <div className="flex-1 mx-4 h-px relative" style={{ background: "rgba(255,255,255,0.15)" }}>
                  <div
                    className="absolute top-0 left-0 h-full transition-all duration-100"
                    style={{ width: `${wipePercent}%`, background: "var(--gold)", boxShadow: "0 0 8px var(--gold)" }}
                  />
                </div>
                <span style={{ opacity: 0.75, color: "var(--gold-2)" }}>After</span>
              </div>
            </div>

            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.36em] text-center" style={{ color: "var(--text-3)" }}>
              ↕ Scroll to wipe the glass
            </div>
          </div>

          {/* Right — story panel (4 cols) */}
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
              Method · DI Water · WFP
            </div>
            <p className="mt-6 font-body" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.7 }}>
              {copy}
            </p>
            <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--line)" }}>
              <div className="font-display leading-none" style={{ fontSize: "clamp(2.5rem, 3.4vw, 3.4rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>
                {stat.value}
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--text-3)" }}>
                {stat.label}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
